import React, { useState, useRef, useEffect } from 'react';
import { BsFillArrowUpCircleFill, BsXCircleFill } from 'react-icons/bs';
import useStore from '../../store/useStore';
import MessageItem from './MessageItem';
import { useNavigate } from 'react-router-dom';

/**
 * Componente para el área de chat donde se muestran los mensajes
 * y se permite enviar nuevos mensajes al agente seleccionado
 */
const ChatArea = () => {
  const navigate = useNavigate();
  const { 
    activeChat, 
    selectedAgent,
    agents,
    sendMessage,
    isTyping,
    createNewChat,
    setSelectedAgent,
    userProfile // Obtener el perfil del usuario del store
  } = useStore();
  
  const [inputMessage, setInputMessage] = useState('');
  
  const [showWelcome, setShowWelcome] = useState(true);

  // Referencias para manipular el DOM
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  

  // Auto-focus en el input al montar el componente
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [activeChat?.id]);

  // Scroll automático al recibir nuevos mensajes o cuando aparece/desaparece el indicador de "escribiendo"
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeChat?.messages, isTyping]);

  // Eliminar el mensaje de bienvenida cuando el usuario escriba su primer mensaje
  useEffect(() => {
    if (activeChat?.messages?.length > 0) {
      setShowWelcome(false);
    } else {
      setShowWelcome(true);
    }
  }, [activeChat?.messages]);

  // Obtener información del agente seleccionado
  const selectedAgentData = React.useMemo(() => {
    const agent = agents.find(a => a.key === selectedAgent);
    console.log('Agente encontrado:', agent);
    return agent || {};
  }, [agents, selectedAgent]);
  
  // Debug: Verificar los datos del agente
  console.log('Agente seleccionado (key):', selectedAgent);
  console.log('Datos completos del agente:', selectedAgentData);

  // Manejar el envío de un nuevo mensaje
  const handleSendMessage = async (e) => {
    e.preventDefault();
    const messageText = inputMessage.trim(); // Usamos una nueva variable para el texto
    if (!messageText) return;

    // Resetear el input INMEDIATAMENTE
    setInputMessage(''); 
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.overflowY = 'hidden';
    } // También resetear la altura y overflow
    
    // Ahora, enviar el mensaje (que ya capturamos en messageText) usando el store
    await sendMessage(messageText, { 
      timestamp: new Date().toISOString(),
      agentId: selectedAgent
    });
    
    // El input ya fue reseteado, no es necesario hacerlo de nuevo aquí.
  };

  // Manejar el envío de mensaje con Enter (pero permitir nueva línea con Shift+Enter)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // Ajustar dinámicamente la altura del textarea según el contenido
  const handleTextareaChange = (e) => {
    setInputMessage(e.target.value);
    
    // Ajustar altura dinámicamente
    const textarea = e.target;
    textarea.style.height = 'auto'; // Reset height to recalculate scrollHeight correctly
    const newHeight = Math.min(textarea.scrollHeight, 150); // Máximo 150px de altura
    textarea.style.height = `${newHeight}px`;

    if (textarea.scrollHeight > 150) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  };

  // Si no hay agente seleccionado, redirigir a la selección
  if (!selectedAgent) {
    navigate('/');
    return null;
  }

  // Si no hay chat activo, mostrar un mensaje de carga
  if (!activeChat) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Preparando el chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Área de mensajes con scrollbar oculto */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar flex flex-col items-center"
        aria-live="polite"
      >
        <div className="w-full max-w-3xl space-y-4"> {/* Contenedor para limitar el ancho del contenido del chat */}
          {/* Mensaje de bienvenida (solo se muestra si no hay mensajes Y showWelcome es true) */}
          {activeChat.messages.length === 0 && showWelcome ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
                {`¡Bienvenido al chat con el ${selectedAgentData?.name || 'Agente'}!`}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-md">
                {selectedAgentData?.welcomeMessage || '¿En qué puedo ayudarte hoy?'}
              </p>
              <button 
                onClick={() => setShowWelcome(false)}
                className="mt-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Cerrar mensaje de bienvenida"
              >
                <BsXCircleFill size={20} />
              </button>
            </div>
          ) : (
            // Si no se muestra el mensaje de bienvenida grande, se muestran los mensajes del chat.
            // El método map devuelve un array de elementos JSX, lo cual es válido aquí.
            activeChat?.messages?.map((message, index) => {
              const isUser = message.sender === 'user';
              const agentForMessage = !isUser ? selectedAgentData : null;
              return (
                <MessageItem 
                  key={message.id || index} 
                  message={message} 
                  isUser={isUser} 
                  agentData={agentForMessage} // Prop 'agent' no se usa en MessageItem, se usa agentData
                  userName={isUser ? (userProfile?.name || 'Tú') : undefined} // Pasar el nombre real o "Tú" como fallback
                />
              );
            })
          )}

          {/* Indicador de escribiendo... (fuera del ternario, dentro del div max-w-3xl) */}
          {isTyping && (
            <div className="flex items-center space-x-1 p-3 max-w-[60px] bg-gray-100 dark:bg-gray-800 rounded-lg self-start">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-1"></span>
            </div>
          )}

          {/* Elemento invisible para hacer scroll automático (fuera del ternario, dentro del div max-w-3xl) */}
          <div ref={messagesEndRef} />
        </div> {/* Cierre del div w-full max-w-3xl space-y-4 */}
      </div> {/* Cierre del div flex-1 overflow-y-auto */}

      {/* Área de entrada de mensaje con autoexpansión */}
      <div className="border-t border-gray-400 dark:border-gray-700 p-6 min-h-[110px]">
        <form onSubmit={handleSendMessage} className="max-w-3xl mx-auto">
          <div className="flex items-end space-x-2 bg-white dark:bg-gray-800 rounded-2xl p-4 border-2 border-blue-500 focus-within:border-blue-500 dark:focus-within:border-blue-500 focus-within:ring-0.5 focus-within:ring-blue-300 dark:focus-within:ring-blue-600 shadow-md dark:shadow-[0_1px_4px_0_rgba(0,0,0,0.18)]">
            <textarea
              ref={inputRef}
              rows="1"
              value={inputMessage}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyPress}
              placeholder={`Escribe un mensaje al ${selectedAgentData?.name || 'Agente'}...`}
              className="flex-1 p-2 bg-transparent dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none focus:ring-0 focus:outline-none custom-scrollbar"
              
              maxLength={4000} // Limitar el número de caracteres
            />
            <button
              type="submit"
              disabled={!inputMessage.trim()}
              className={`p-1 rounded-full transition-colors ${
                !inputMessage.trim() 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300'
              }`}
              aria-label="Enviar mensaje"
            >
              <BsFillArrowUpCircleFill size={24} />
            </button>
          </div>
          
          {/* Contador de caracteres - solo mostrar si está cerca del límite */}
          {inputMessage.length > 3000 && (
            <div className="text-xs text-right mt-1 text-gray-500 dark:text-gray-400 px-2">
              {`${inputMessage.length}/4000 caracteres`}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ChatArea;
